import {useMemo, useState} from 'react';
import {
  Check,
  X,
  Pencil,
  Lock,
  ChevronRight,
  Users,
  AlertTriangle,
  Inbox,
  Search,
  Eye,
  EyeOff,
  ExternalLink,
  MoreHorizontal
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Card,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  FieldDescription,
  FieldLegend,
  FieldSet,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Spinner,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {type ConfigItem} from '@/api/config';
import {type Feedback, type FeedbackKind} from '@/api/feedback';
import {type UserProfile, type Role, usersApi} from '@/api/users';
import {
  useHopperConfigs,
  useClassificationConfigs,
  useFeedbackSubmissions,
  useHideFeedback,
  useMarkFeedbackJiraCreated,
  useUnhideFeedback
} from '@/api/hooks';
import {buildLocationPaths, buildLocationTree} from '@/components/classify/locations';
import {getApiErrorMessage} from '@/api/client';
import {HopperAdmin} from '@/components/admin/HopperAdmin';
import {ModerationSettingsCard} from '@/components/admin/ModerationSettingsCard';
import {PromptsAdmin} from '@/components/admin/PromptsAdmin';
import {ClassificationsAdmin} from '@/components/admin/ClassificationsAdmin';
import {EscalationAdmin} from '@/components/admin/EscalationAdmin';
import {TopicsAdmin} from '@/components/admin/TopicsAdmin';
import {CustomerFeedbackSourcesAdmin} from '@/components/admin/CustomerFeedbackSourcesAdmin';
import {SwitchesAdmin} from '@/components/admin/SwitchesAdmin';
import {PLATFORM_CODENAME} from '@/lib/platform';
import {ADMIN_SECTIONS, type AdminSectionSlug, adminSection} from '@/lib/adminSections';
import {TablePanel} from '@/components/ui/table-panel';
import {showToast} from '@/lib/toast';
import {useDebounce} from '@/lib/useDebounce';

// The reference-data sections, keyed to the lists AdminPage loads for them.
type ConfigSection = Extract<AdminSectionSlug, 'languages' | 'topics' | 'core-risks' | 'locations'>;

type Props = {
  readonly section: AdminSectionSlug;
  readonly initialLanguages: ConfigItem[];
  readonly initialTopics: ConfigItem[];
  readonly initialLocations: ConfigItem[];
  readonly initialCoreRisks: ConfigItem[];
  readonly initialUsers: UserProfile[];
  readonly currentUserEmail: string;
  readonly verticals: string[];
};

// One settings section, picked by the route (/admin/<section>). The sections used to share an inner
// settings rail on a single Admin page; each now has its own sidebar entry and URL instead.
export function ConfigAdmin({
  section,
  initialLanguages,
  initialTopics,
  initialLocations,
  initialCoreRisks,
  initialUsers,
  currentUserEmail,
  verticals
}: Props) {
  const [query, setQuery] = useState('');
  // Moving between sections keeps this component mounted, so the filter is cleared here rather than
  // carried over to a section it was never typed for.
  const [querySection, setQuerySection] = useState(section);
  if (querySection !== section) {
    setQuerySection(section);
    setQuery('');
  }

  const [users, setUsers] = useState<UserProfile[]>(initialUsers);
  const {data: hoppersData = []} = useHopperConfigs();
  const classificationConfigs = useClassificationConfigs();
  const [items] = useState<Record<ConfigSection, ConfigItem[]>>({
    languages: initialLanguages,
    topics: initialTopics,
    locations: initialLocations,
    'core-risks': initialCoreRisks
  });
  const isConfigSection =
    section === 'languages' ||
    section === 'topics' ||
    section === 'core-risks' ||
    section === 'locations';

  const current = isConfigSection ? items[section] : [];
  const activeSection = adminSection(section) ?? ADMIN_SECTIONS[0]!;
  const isSearchableSection = isConfigSection || section === 'users';

  // Filtering waits for typing to pause, but a cleared box (including the reset on a section change)
  // applies at once, so a new section never shows a moment filtered by the last one's term.
  const debouncedQuery = useDebounce(query, 300);
  const q = query === '' ? '' : debouncedQuery.trim().toLowerCase();
  const filteredCurrent = q
    ? current.filter((item) => item.name.toLowerCase().includes(q))
    : current;
  const filteredUsers = q
    ? users.filter(
        (u) => u.display_name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
      )
    : users;

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        {/* The section's name is already the page header's title, so only its hint is repeated. */}
        <p className="text-muted-foreground text-xs">{activeSection.hint}</p>
        {isSearchableSection ? (
          <InputGroup className="w-56 max-w-full shrink-0">
            <InputGroupAddon>
              <Search />
            </InputGroupAddon>
            <InputGroupInput
              aria-label={`Filter ${activeSection.label.toLowerCase()}`}
              placeholder={`Filter ${activeSection.label.toLowerCase()}…`}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
              }}
            />
          </InputGroup>
        ) : null}
      </div>

      {section === 'classifications' ? (
        classificationConfigs.isLoading ? (
          <div className="text-muted-foreground flex items-center gap-2 text-xs">
            <Spinner /> Loading…
          </div>
        ) : (
          <ClassificationsAdmin initialConfigs={classificationConfigs.data ?? []} />
        )
      ) : section === 'feedback' ? (
        <FeedbackList />
      ) : section === 'customer-feedback-sheets' ? (
        <CustomerFeedbackSourcesAdmin />
      ) : section === 'escalation' ? (
        <EscalationAdmin verticals={verticals} />
      ) : section === 'prompts' ? (
        <PromptsAdmin />
      ) : section === 'delivery' ? (
        <HopperAdmin initialConfigs={hoppersData} verticals={verticals} />
      ) : section === 'queues' ? (
        <ModerationSettingsCard />
      ) : section === 'switches' ? (
        <SwitchesAdmin />
      ) : section === 'users' ? (
        <UsersList
          users={filteredUsers}
          currentUserEmail={currentUserEmail}
          verticals={verticals}
          filtered={q !== ''}
          onUpdate={(u) => {
            setUsers((previous) => previous.map((x) => (x.id === u.id ? u : x)));
          }}
        />
      ) : section === 'topics' ? (
        <TopicsAdmin initialTopics={items.topics} normalisedQuery={q} />
      ) : section === 'locations' ? (
        <>
          <PackOwnedNotice count={current.length} filteredCount={filteredCurrent.length} />
          <LocationList items={current} normalisedQuery={q} />
        </>
      ) : section === 'core-risks' ? (
        <>
          <PackOwnedNotice count={current.length} filteredCount={filteredCurrent.length} />
          <CoreRiskList items={filteredCurrent} filtered={q !== ''} />
        </>
      ) : (
        <>
          <PackOwnedNotice count={current.length} filteredCount={filteredCurrent.length} />
          <SimpleList items={filteredCurrent} filtered={q !== ''} />
        </>
      )}
    </div>
  );
}

// ── Supporting List Components ───────────────────────────────────────────────

/**
 Explains why the three pack-owned reference-data tabs have no controls. Topics are not among
 them — nothing upstream produces topics, so they are managed in `TopicsAdmin`.
 */
function PackOwnedNotice({
  count,
  filteredCount
}: {
  readonly count: number;
  readonly filteredCount?: number;
}) {
  const label =
    filteredCount !== undefined && filteredCount !== count
      ? `${filteredCount.toLocaleString()} of ${count.toLocaleString()} entries`
      : `${count.toLocaleString()} entries`;

  return (
    <Alert>
      <Lock />
      <AlertTitle>Reference data — read only ({label})</AlertTitle>
      <AlertDescription>
        Authored off-platform and applied from a versioned data pack on every deploy. To change it,
        change the pack — an edit made here would be reverted by the next rollout.
      </AlertDescription>
    </Alert>
  );
}

/** Pack entries are deprecated rather than deleted, so a retired row still lists - marked. */
function EntryStatusBadge({status}: {readonly status: string}) {
  return status === 'active' ? (
    <Badge variant="secondary">Active</Badge>
  ) : (
    <Badge variant="outline" className="capitalize">
      {status}
    </Badge>
  );
}

function SimpleList({
  items,
  filtered
}: {
  readonly items: ConfigItem[];
  readonly filtered?: boolean;
}) {
  if (items.length === 0) {
    return filtered ? <NoMatches /> : <EmptyPack />;
  }

  return (
    <TablePanel>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Code</TableHead>
            <TableHead className="w-28">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id}>
              <TableCell className="font-medium">{item.name}</TableCell>
              <TableCell>
                <MetadataChips metadata={item.metadata} />
              </TableCell>
              <TableCell>
                <EntryStatusBadge status={item.status} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TablePanel>
  );
}

// Jira Cloud honours the same pid/issuetype/summary/description query params documented for
// Server's CreateIssueDetails!init.jspa deep link - see
// https://confluence.atlassian.com/jirakb/how-to-create-issues-using-direct-html-links-in-jira-server-159474.html
const JIRA_BASE_URL = 'https://crispthinking1.atlassian.net';
const JIRA_GT_PROJECT_ID = '10460';
// GT's issue type ids: a bug report becomes a Bug, everything else a Story.
const JIRA_ISSUE_TYPE_IDS: Record<FeedbackKind, string> = {feedback: '10001', bug: '1'};
// Numeric id of the "PRI: Feedback" epic (GT-16895), which sits under the PRI initiative (GT-15382).
// Jira's deep link takes the parent's id, not its key.
const JIRA_FEEDBACK_EPIC_ID = '125636';
// Keeps the built URL well under browser/server query-string limits even for the longest
// allowed feedback message (FeedbackCreateRequest.Message caps at 20,000 chars).
const JIRA_DESCRIPTION_MESSAGE_LIMIT = 500;

export function buildJiraCreateIssueUrl(item: Feedback): string {
  const truncated =
    item.message.length > JIRA_DESCRIPTION_MESSAGE_LIMIT
      ? `${item.message.slice(0, JIRA_DESCRIPTION_MESSAGE_LIMIT)}…`
      : item.message;
  const isBug = item.kind === 'bug';
  const details = [
    item.rating === null ? null : `Rating: ${item.rating}/5`,
    item.impact === null ? null : `Impact: ${item.impact}`,
    item.url === null ? null : `URL: ${item.url}`,
    `Submitted by ${item.submitted_by} on ${new Date(item.created_at).toLocaleString()} from ${item.page}`,
    `Platform: ${PLATFORM_CODENAME}`,
    item.last_error === null ? null : `Last API error: ${item.last_error}`
  ].filter((line) => line !== null);
  const params = new URLSearchParams({
    pid: JIRA_GT_PROJECT_ID,
    issuetype: JIRA_ISSUE_TYPE_IDS[item.kind],
    parentIssueId: JIRA_FEEDBACK_EPIC_ID,
    summary: isBug ? `Bug: ${item.title ?? item.page}` : `Feedback: ${item.page}`,
    description: `${truncated}\n\n—\n${details.join('\n')}`
  });
  return `${JIRA_BASE_URL}/secure/CreateIssueDetails!init.jspa?${params.toString()}`;
}

type FeedbackTab = FeedbackKind | 'all';

const FEEDBACK_TABS: ReadonlyArray<{value: FeedbackTab; label: string}> = [
  {value: 'all', label: 'All'},
  {value: 'feedback', label: 'Feedback'},
  {value: 'bug', label: 'Bugs'}
];

function FeedbackList() {
  const [showHidden, setShowHidden] = useState(false);
  const [tab, setTab] = useState<FeedbackTab>('all');
  const {data: allItems = []} = useFeedbackSubmissions(showHidden);
  const items = tab === 'all' ? allItems : allItems.filter((item) => item.kind === tab);
  const hideFeedback = useHideFeedback();
  const unhideFeedback = useUnhideFeedback();
  const markJiraCreated = useMarkFeedbackJiraCreated();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Tabs
          value={tab}
          onValueChange={(value) => {
            setTab(value as FeedbackTab);
          }}>
          <TabsList aria-label="Feedback type">
            {FEEDBACK_TABS.map((option) => (
              <TabsTrigger key={option.value} value={option.value}>
                {option.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Button
          variant="secondary"
          size="sm"
          aria-pressed={showHidden}
          onClick={() => {
            setShowHidden((prev) => !prev);
          }}>
          {showHidden ? <EyeOff /> : <Eye />}
          {showHidden ? 'Hide dismissed' : 'Show hidden'}
        </Button>
      </div>

      {items.length === 0 ? (
        <Empty className="border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Inbox />
            </EmptyMedia>
            <EmptyTitle>
              {showHidden ? 'No feedback submitted yet' : 'No feedback to triage'}
            </EmptyTitle>
          </EmptyHeader>
        </Empty>
      ) : (
        <Card className="divide-border gap-0 divide-y overflow-hidden p-0">
          {items.map((item) => (
            <div key={item.id} className="flex flex-col gap-1 px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 font-medium">
                  {item.submitted_by}
                  {item.kind === 'bug' ? (
                    <Badge variant="destructive">Bug</Badge>
                  ) : (
                    <Badge variant="secondary">Feedback</Badge>
                  )}
                  {item.impact === null ? null : <Badge variant="outline">{item.impact}</Badge>}
                  {item.rating === null ? null : (
                    <Badge variant="outline" aria-label={`Rated ${item.rating} out of 5`}>
                      {'★'.repeat(item.rating)}
                    </Badge>
                  )}
                  {item.hidden ? <Badge variant="outline">Hidden</Badge> : null}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground text-xs">
                    {new Date(item.created_at).toLocaleString()}
                  </span>
                  <a
                    href={buildJiraCreateIssueUrl(item)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-ink inline-flex items-center gap-1 text-xs font-medium hover:underline"
                    onClick={() => {
                      markJiraCreated.mutate(item.id);
                    }}>
                    <ExternalLink className="size-3" />
                    Create in Jira
                  </a>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={item.hidden ? 'Unhide' : 'Dismiss'}
                          onClick={() => {
                            const verb = item.hidden ? 'unhide' : 'hide';
                            (item.hidden ? unhideFeedback : hideFeedback).mutate(item.id, {
                              onError(error) {
                                showToast(
                                  getApiErrorMessage(error, `Failed to ${verb} feedback.`),
                                  'error'
                                );
                              }
                            });
                          }}
                        />
                      }>
                      <X />
                    </TooltipTrigger>
                    <TooltipContent>{item.hidden ? 'Unhide' : 'Dismiss'}</TooltipContent>
                  </Tooltip>
                </div>
              </div>
              <p className="text-muted-foreground text-xs break-all">{item.url ?? item.page}</p>
              {item.title === null ? null : <p className="font-medium">{item.title}</p>}
              {item.message === '' ? null : <p className="whitespace-pre-wrap">{item.message}</p>}
              {item.last_error === null ? null : (
                <p className="text-destructive-ink font-mono text-xs break-all">
                  Last API error: {item.last_error}
                </p>
              )}
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

function CoreRiskList({
  items,
  filtered
}: {
  readonly items: ConfigItem[];
  readonly filtered?: boolean;
}) {
  if (items.length === 0) {
    return filtered ? <NoMatches /> : <EmptyPack />;
  }

  return (
    <TablePanel>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Taxonomy mappings</TableHead>
            <TableHead className="w-28">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => {
            const mappings = (item.metadata.taxonomy_mappings ?? []) as string[];
            return (
              <TableRow key={item.id}>
                <TableCell className="font-medium">{item.name}</TableCell>
                <TableCell>
                  {mappings.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {mappings.map((m) => (
                        <Badge key={m} variant="outline">
                          {m}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <EntryStatusBadge status={item.status} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TablePanel>
  );
}

/**
 The location hierarchy, to whatever depth the pack has.
 */
// The reference data is four levels deep - UN M.49 region, sub-region, intermediate region, then
// ISO 3166-1 country - so this recurses. A fixed two levels would leave most countries unlisted
// here, which is how the classify picker used to behave before it was made recursive too.
function LocationList({
  items,
  normalisedQuery
}: {
  readonly items: ConfigItem[];
  readonly normalisedQuery: string;
}) {
  const {roots, childrenById} = useMemo(() => buildLocationTree(items), [items]);
  // Ancestor path for a search hit - once countries sit under sub-regions, a bare name can mean
  // nothing alone ("Georgia" is a country and a US state), so a flat filtered list needs it.
  const pathById = useMemo(() => buildLocationPaths(items), [items]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  if (items.length === 0) {
    return <EmptyPack />;
  }

  if (normalisedQuery) {
    const matches = items.filter((l) => l.name.toLowerCase().includes(normalisedQuery));

    if (matches.length === 0) {
      return <NoMatches />;
    }

    return (
      <TablePanel>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="w-24">Code</TableHead>
              <TableHead className="w-28">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {matches.map((item) => {
              const countryCode = item.metadata.countryCode as string | undefined;
              const path = pathById.get(item.id);
              return (
                <TableRow key={item.id}>
                  <TableCell>
                    <span className="font-medium">{item.name}</span>
                    {path ? <p className="text-muted-foreground truncate text-xs">{path}</p> : null}
                  </TableCell>
                  <TableCell>
                    {countryCode ? (
                      <span className="text-muted-foreground font-mono">{countryCode}</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <EntryStatusBadge status={item.status} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TablePanel>
    );
  }

  return (
    <TablePanel>
      {roots.map((root) => (
        <LocationBranch
          key={root.id}
          item={root}
          depth={0}
          childrenById={childrenById}
          expanded={expanded}
          onToggle={toggle}
        />
      ))}
    </TablePanel>
  );
}

function LocationBranch({
  item,
  depth,
  childrenById,
  expanded,
  onToggle
}: {
  readonly item: ConfigItem;
  readonly depth: number;
  readonly childrenById: Map<string, ConfigItem[]>;
  readonly expanded: ReadonlySet<string>;
  readonly onToggle: (id: string) => void;
}) {
  const children = childrenById.get(item.id) ?? [];
  const isExpanded = expanded.has(item.id);
  const countryCode = item.metadata.countryCode as string | undefined;

  if (children.length === 0) {
    return (
      <div
        className="flex items-center gap-2 border-b py-2 pr-4 last:border-0"
        style={{paddingLeft: AdminIndent + depth * AdminDepthIndent + AdminChevronWidth}}>
        <span className="flex-1">{item.name}</span>
        {countryCode ? (
          <span className="text-muted-foreground font-mono text-xs">{countryCode}</span>
        ) : null}
      </div>
    );
  }

  // Controlled, because the expanded set lives on the tree (a search resets it), not on the branch.
  // `block` because the panel sits flush under its trigger here, not 8px below it.
  return (
    <Collapsible
      open={isExpanded}
      className="block border-b last:border-0"
      onOpenChange={() => {
        onToggle(item.id);
      }}>
      <CollapsibleTrigger
        render={
          <button
            type="button"
            className="hover:bg-muted/50 flex w-full cursor-pointer items-center gap-2 py-2.5 pr-4 text-left transition-colors select-none"
            style={{paddingLeft: AdminIndent + depth * AdminDepthIndent}}
          />
        }>
        <ChevronRight className="text-muted-foreground size-4 shrink-0 transition-transform duration-200 in-data-panel-open:rotate-90" />
        <span className="flex-1 font-medium">{item.name}</span>
        <Badge variant="outline" className="tabular-nums">
          {children.length}
        </Badge>
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t">
        {children.map((child) => (
          <LocationBranch
            key={child.id}
            item={child}
            depth={depth + 1}
            childrenById={childrenById}
            expanded={expanded}
            onToggle={onToggle}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

function EmptyPack() {
  return (
    <Empty className="border">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <AlertTriangle />
        </EmptyMedia>
        <EmptyTitle>No entries applied</EmptyTitle>
        <EmptyDescription>
          The reference-data pack is applied on deploy. An empty list means the loader has not run
          against this database yet.
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}

/** Shown instead of the tab's usual empty state once a search filter is what emptied the list. */
function NoMatches() {
  return (
    <Empty className="border">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Search />
        </EmptyMedia>
        <EmptyTitle>No matches</EmptyTitle>
        <EmptyDescription>
          Nothing matches this filter. Try a different search term.
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}

/** Renders whatever scalar metadata a pack entry carries, e.g. a language code. */
function MetadataChips({metadata}: {readonly metadata: Record<string, unknown>}) {
  const chips = Object.entries(metadata)
    .filter(([key, value]) => DisplayedMetadataKeys.has(key) && typeof value === 'string')
    .map(([key, value]) => `${key}: ${value as string}`);

  if (chips.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }

  return <span className="text-muted-foreground font-mono text-xs">{chips.join('  ')}</span>;
}

const DisplayedMetadataKeys = new Set(['code', 'iso639_1', 'iso639_3', 'countryCode']);

const AdminIndent = 16;
const AdminDepthIndent = 16;
const AdminChevronWidth = 24;

const ROLE_OPTIONS: Array<{
  value: Role;
  label: string;
  variant: 'default' | 'secondary' | 'outline';
}> = [
  {value: 'analyst', label: 'Analyst', variant: 'outline'},
  {value: 'analyst_admin', label: 'Analyst Admin', variant: 'secondary'},
  {value: 'admin', label: 'Admin', variant: 'default'}
];

// User, role, queue access, QC gate, P0 approval and the actions menu.
const USERS_COLUMN_COUNT = 6;

function UsersList({
  users,
  currentUserEmail,
  onUpdate,
  verticals,
  filtered
}: {
  readonly users: UserProfile[];
  readonly currentUserEmail: string;
  readonly onUpdate: (u: UserProfile) => void;
  readonly verticals: string[];
  readonly filtered?: boolean;
}) {
  if (users.length === 0) {
    return filtered ? (
      <NoMatches />
    ) : (
      <Empty className="border">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Users />
          </EmptyMedia>
          <EmptyTitle>No active user accounts</EmptyTitle>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <TablePanel>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Queue access</TableHead>
            <TableHead>QC gate</TableHead>
            <TableHead>P0 approval</TableHead>
            <TableHead className="w-6">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <UserRow
              key={user.id}
              user={user}
              isSelf={user.email === currentUserEmail}
              verticals={verticals}
              onUpdate={onUpdate}
            />
          ))}
        </TableBody>
      </Table>
    </TablePanel>
  );
}

function UserRow({
  user,
  isSelf,
  onUpdate,
  verticals
}: {
  readonly user: UserProfile;
  readonly isSelf: boolean;
  readonly onUpdate: (u: UserProfile) => void;
  readonly verticals: string[];
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingVerticals, setEditingVerticals] = useState(false);
  const [selectedVerticals, setSelectedVerticals] = useState<Set<string>>(
    new Set(user.allowed_verticals)
  );

  const initials = user.display_name
    .split(' ')
    .map((n) => n.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
  const current = ROLE_OPTIONS.find((r) => r.value === user.role);
  const hasAllVerticals = user.allowed_verticals === null;
  // Role is Auth0-managed and read-only here; manage it in the Auth0 dashboard, not this UI. A
  // "No role" badge means Auth0 has assigned this user none of the three valid roles, so they
  // authenticate but currently have no [Authorize(Roles = ...)] access at all - deliberately not a
  // ROLE_OPTIONS fallback, so it reads as "needs attention" rather than silently showing Analyst.
  const handleVerticalsUpdate = async () => {
    setSaving(true);
    setError(null);
    try {
      const verticalsArray = [...selectedVerticals];
      const updated = await usersApi.updateVerticals(
        user.id,
        verticalsArray.length === 0 ? null : verticalsArray
      );
      onUpdate(updated);
      setEditingVerticals(false);
    } catch (error_) {
      setError(getApiErrorMessage(error_, "Couldn't update verticals."));
    } finally {
      setSaving(false);
    }
  };

  const handleSkipP0ApprovalChange = async (skipP0Approval: boolean) => {
    setSaving(true);
    setError(null);
    try {
      const updated = await usersApi.updateSkipP0Approval(user.id, skipP0Approval);
      onUpdate(updated);
    } catch (error_) {
      setError(getApiErrorMessage(error_, "Couldn't update the P0 approval setting."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <div className="flex min-w-0 items-center gap-2.5">
            <Avatar size="sm">
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="truncate font-medium">{user.display_name}</span>
                {isSelf ? <Badge variant="outline">You</Badge> : null}
              </div>
              <p className="text-muted-foreground truncate text-xs">{user.email}</p>
            </div>
          </div>
        </TableCell>
        <TableCell>
          {current ? (
            <Badge variant={current.variant}>{current.label}</Badge>
          ) : (
            <span className="text-destructive-ink font-medium">No role</span>
          )}
        </TableCell>
        <TableCell>
          <div className="flex flex-wrap gap-1">
            {hasAllVerticals ? (
              <Badge variant="secondary">All verticals</Badge>
            ) : user.allowed_verticals && user.allowed_verticals.length > 0 ? (
              user.allowed_verticals.map((v) => (
                <Badge key={v} variant="outline">
                  {v}
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground">None assigned</span>
            )}
          </div>
        </TableCell>
        <TableCell>
          {user.requires_qc_review ? (
            <Badge variant="secondary">QC gated</Badge>
          ) : (
            <Badge variant="outline">No gate</Badge>
          )}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <Switch
              size="sm"
              checked={user.skip_p0_approval}
              disabled={saving}
              aria-label={`Skip P0 approval for ${user.display_name}`}
              onCheckedChange={(checked) => void handleSkipP0ApprovalChange(checked)}
            />
            <span className="text-muted-foreground text-xs">
              {user.skip_p0_approval ? 'Auto-delivers own P0s' : 'Needs approval'}
            </span>
          </div>
        </TableCell>
        <TableCell>
          {/* Queue access is the menu's only action, so the menu is left out on your own row (and
              while the editor is open) instead of offering a disabled item. */}
          {isSelf || editingVerticals ? null : (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Actions for ${user.display_name}`}
                  />
                }>
                <MoreHorizontal />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    setSelectedVerticals(new Set(user.allowed_verticals));
                    setEditingVerticals(true);
                  }}>
                  <Pencil />
                  Edit queue access
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </TableCell>
      </TableRow>
      {editingVerticals || error ? (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={USERS_COLUMN_COUNT} className="bg-muted/30">
            <div className="flex flex-col gap-2">
              {editingVerticals ? (
                <>
                  <FieldSet className="gap-2">
                    <FieldLegend variant="label" className="mb-0">
                      Queue access
                    </FieldLegend>
                    <FieldDescription>None selected grants every vertical.</FieldDescription>
                    <ToggleGroup
                      multiple
                      variant="outline"
                      spacing={0}
                      size="sm"
                      className="flex-wrap"
                      aria-label="Queue access"
                      value={[...selectedVerticals]}
                      onValueChange={(next) => {
                        setSelectedVerticals(new Set(next));
                      }}>
                      {verticals.map((v) => (
                        <ToggleGroupItem key={v} value={v}>
                          {v}
                        </ToggleGroupItem>
                      ))}
                    </ToggleGroup>
                  </FieldSet>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={saving}
                      onClick={() => {
                        setEditingVerticals(false);
                        setSelectedVerticals(new Set(user.allowed_verticals));
                      }}>
                      <X /> Cancel
                    </Button>
                    <Button
                      size="sm"
                      disabled={saving}
                      onClick={() => void handleVerticalsUpdate()}>
                      <Check /> Save changes
                    </Button>
                  </div>
                </>
              ) : null}
              {error ? <p className="text-destructive-ink text-xs">{error}</p> : null}
            </div>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}
